import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useDefaultFields } from '@shared/scenes/entity/create/hooks/useDefaultFields';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { STATIC_COMPONENT_IDS } from '@shared/scenes/workflows/template/ComponentOptionDefaults';
import { isInputComponent } from '@shared/scenes/workflows/utils';

export function useWorkflowSupportsQuickCreate({
  workflowTemplateId,
}: {
  workflowTemplateId?: string | null;
}) {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId ?? undefined);
  const { components } = workflowTemplate ?? {};
  const defaultFields = useDefaultFields({ workflowTemplateId, isViewTemplateRequired: false });

  return useMemo(() => {
    /* We hack quick create to only set the name of the related entity. This has a few downsides
     *  - Name isn't guaranteed, so we must verify it exists.
     *  - There may be other required fields that we cannot set.
     *
     *  If there isn't a name field or there are other required fields, we will not allow creation
     */
    const relatedWorkflowHasName =
      components?.some(c => c.id === STATIC_COMPONENT_IDS.NAME) ?? false;

    const relatedWorkflowHasOtherRequiredFields = components
      ?.filter(isInputComponent)
      .filter(c => c.id !== STATIC_COMPONENT_IDS.NAME)
      .some(c => {
        const hasDefault = !isEmpty(defaultFields?.[c.id]);
        const isRequired = c.required;

        return isRequired && !hasDefault;
      });
    return relatedWorkflowHasName && !relatedWorkflowHasOtherRequiredFields;
  }, [components, defaultFields]);
}
